/**
 * Sticky-bar variant row (see /snippets/sticky-variant-row.liquid).
 *
 * The row is a proxy for the in-page radio inputs rendered by
 * `.product__selectors`: a click forwards to the matching input and fires a
 * `change`, so the theme's own ProductForm does all the real work (variant id,
 * price, compare-at, sold-out state). The resulting state is mirrored back onto
 * the buttons.
 *
 * Used by the Landing header bar and the product page's sticky bar — both live
 * outside the <form>, so neither can hold real inputs.
 */

const ROW_SELECTOR = '.sticky-variants';
const OPTION_GROUP = '[data-sticky-option-position]';
const OPTION_BUTTON = '[data-sticky-option-value]';
const SELECTORS_ROOT = '.product__selectors';

/**
 * The row and the inputs it drives are always in the same Shopify section, but
 * the sticky bar can sit far from the form in the DOM — hence the section-scoped
 * lookup with a document-wide fallback for templates that render the bar outside.
 */
function findSelectorsRoot(row) {
    const section = row.closest('.shopify-section');
    return (section && section.querySelector(SELECTORS_ROOT)) || document.querySelector(SELECTORS_ROOT);
}

class StickyVariants {
    constructor(row) {
        this.row = row;
        this.selectorsRoot = findSelectorsRoot(row);
        this.buttons = [...row.querySelectorAll(OPTION_BUTTON)];

        if (!this.selectorsRoot || !this.buttons.length) return;

        this.onClick = this.onClick.bind(this);
        this.sync = this.sync.bind(this);

        this.buttons.forEach((btn) => btn.addEventListener('click', this.onClick));
        this.selectorsRoot.addEventListener('change', this.sync);

        // The theme's sellout preview toggles .sold-out / .unavailable on the
        // in-page option wrappers whenever the selection changes.
        this.observer = new MutationObserver(this.sync);
        this.observer.observe(this.selectorsRoot, {
            attributes: true,
            attributeFilter: ['class'],
            subtree: true,
        });

        this.sync();
    }

    findInput(btn) {
        const group = btn.closest(OPTION_GROUP);
        if (!group) return null;

        const position = group.dataset.stickyOptionPosition;
        const value = btn.dataset.stickyOptionValue;

        // Matched on .value rather than a CSS attribute selector — option values
        // can contain quotes and other characters that break selector syntax.
        return (
            [
                ...this.selectorsRoot.querySelectorAll(
                    `[data-single-option-selector][data-index="option${position}"]`
                ),
            ].find((input) => input.value === value) || null
        );
    }

    onClick(event) {
        event.preventDefault();

        const input = this.findInput(event.currentTarget);
        if (!input || input.checked) return;

        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    sync() {
        this.buttons.forEach((btn) => {
            const input = this.findInput(btn);
            if (!input) return;

            // Theme puts the state classes on the .radio__button / .swatch__button
            const wrapper = input.parentElement;

            btn.classList.toggle('is-active', input.checked);
            btn.classList.toggle('is-sold-out', wrapper.classList.contains('sold-out'));
            btn.classList.toggle('is-unavailable', wrapper.classList.contains('unavailable'));
            btn.setAttribute('aria-pressed', input.checked ? 'true' : 'false');
        });
    }

    destroy() {
        this.buttons.forEach((btn) => btn.removeEventListener('click', this.onClick));
        if (this.selectorsRoot) {
            this.selectorsRoot.removeEventListener('change', this.sync);
        }
        if (this.observer) this.observer.disconnect();
    }
}

function init(scope = document) {
    scope.querySelectorAll(ROW_SELECTOR).forEach((row) => {
        if (row._stickyVariants) return;
        row._stickyVariants = new StickyVariants(row);
    });
}

function destroyIn(scope) {
    if (!scope || !scope.querySelectorAll) return;
    scope.querySelectorAll(ROW_SELECTOR).forEach((row) => {
        if (row._stickyVariants) {
            row._stickyVariants.destroy();
            row._stickyVariants = null;
        }
    });
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init());
} else {
    init();
}

document.addEventListener('shopify:section:load', (event) => {
    if (event.target) init(event.target);
});

document.addEventListener('shopify:section:unload', (event) => {
    if (event.target) destroyIn(event.target);
});

export { init, destroyIn };
